import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import {
  catchError,
  defaultIfEmpty,
  filter,
  forkJoin,
  map,
  type Observable,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';

import { type CanvasElementFrom } from '@blaze-runtime/engine-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type WorkflowSchema } from '@blaze-shared/schema-domain';

import { updateElementProps } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import {
  addEventWorkflowWithConnection,
  type AiWorkflowFinalizationEntry,
  bindValueToWorkflowSource,
  DEFAULT_SUPPORTED_INPUTS_MAP,
  type ElementHandler,
  markWorkflowForceUpdateIfWired,
  upsertElement,
} from '../../../ai';
import { AiWorkflowGenerationService, ComponentSourceWorkflowService } from '../../../ai/workflow';
import { ElementDefinitionsService } from '../../../services/element-definitions.service';

import {
  SCHEDULER_INPUT_TOKEN,
  SCHEDULER_RESOLVED_INPUT,
  type SchedulerContext,
  type SchedulerResolvedContext,
} from './scheduler.definition';
import { wireSchedulerWorkflowInputs } from './scheduler.element-handler.utils';

const START_TIME_SCHEMA_TIMEOUT_MS = 10_000;

export class SchedulerElementHandler implements ElementHandler<'scheduler'> {
  private readonly schemaService = inject(SchemaService);
  private readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);
  private readonly componentSourceWorkflowService = inject(ComponentSourceWorkflowService);
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly store = inject(Store);

  readonly type = 'scheduler';

  prepare(context: SchedulerContext): Observable<SchedulerResolvedContext> {
    const sourceWorkflow = context.componentWorkflows[context.element.id];
    const actionWorkflow = context.componentWorkflows[`${context.element.id}#action`];

    if (!sourceWorkflow?.workflow) {
      return of(context);
    }

    const tasks: Observable<Partial<SchedulerResolvedContext>>[] = [
      this.componentSourceWorkflowService
        .ensureCreated(sourceWorkflow, context.projectId, context.pageId)
        .pipe(map((workflowContext) => ({ workflowContext }))),
    ];

    if (actionWorkflow?.workflow) {
      tasks.push(
        this.componentSourceWorkflowService
          .ensureCreated(actionWorkflow, context.projectId, context.pageId)
          .pipe(
            map((actionWorkflowContext) => ({ actionWorkflowContext })),
            catchError((error) => {
              console.warn(
                `Scheduler action workflow creation failed for element ${context.element.id}`,
                error,
              );
              return of<Partial<SchedulerResolvedContext>>({});
            }),
          ),
      );
    }

    return forkJoin(tasks).pipe(map((results) => Object.assign({ ...context }, ...results)));
  }

  process(context: SchedulerResolvedContext, draft: WritableDraft<EngineState>): void {
    const actionWorkflow = context.actionWorkflowContext?.workflow;

    upsertElement(
      draft,
      context,
      actionWorkflow ? { content: { action: { workflowId: actionWorkflow.id } } } : {},
    );

    const workflow = context.workflowContext?.workflow;
    if (!workflow) {
      return;
    }

    const workflowsToFinalize: AiWorkflowFinalizationEntry[] = [];

    bindValueToWorkflowSource(draft, context, workflow);

    workflowsToFinalize.push({ workflow });

    if (actionWorkflow) {
      const { hasUpdates: actionHasUpdates } = addEventWorkflowWithConnection(
        draft,
        context,
        actionWorkflow,
        'action',
        new Map<string, string>([
          ...DEFAULT_SUPPORTED_INPUTS_MAP,
          [SCHEDULER_INPUT_TOKEN, SCHEDULER_RESOLVED_INPUT],
        ]),
      );

      workflowsToFinalize.push({ workflow: actionWorkflow, forceUpdate: actionHasUpdates });
    }

    context.workflowsToFinalize = workflowsToFinalize;
  }

  wireCrossComponentInputs(
    context: SchedulerResolvedContext,
    draft: WritableDraft<EngineState>,
  ): void {
    const workflow = context.workflowContext?.workflow;
    if (!workflow) {
      return;
    }

    const hasUpdates = wireSchedulerWorkflowInputs(
      draft,
      context,
      workflow,
      this.elementDefinitions,
    );
    markWorkflowForceUpdateIfWired(context.workflowsToFinalize, workflow, hasUpdates);
  }

  finalize(
    element: CanvasElementFrom<'scheduler'>,
    context: SchedulerResolvedContext,
  ): Observable<void> {
    return this.aiWorkflowGenerationService
      .handleAiGeneratedWorkflows(context.workflowsToFinalize ?? [])
      .pipe(switchMap(() => this.bindStartTimeColumn(element, context)));
  }

  private bindStartTimeColumn(
    element: CanvasElementFrom<'scheduler'>,
    context: SchedulerResolvedContext,
  ): Observable<void> {
    const workflow = context.workflowContext?.workflow;
    if (!workflow) {
      return of(undefined);
    }

    return this.schemaService.observeWorkflowSchema(workflow.id).pipe(
      map((schema: WorkflowSchema | undefined) =>
        schema ? schema.columns.find((c) => c.name.startsWith('startTime'))?.name : undefined,
      ),
      filter((columnName): columnName is string => !!columnName),
      take(1),
      defaultIfEmpty<string, undefined>(undefined),
      timeout({
        first: START_TIME_SCHEMA_TIMEOUT_MS,
        with: () => of<string | undefined>(undefined),
      }),
      tap((startTimeColumn) => {
        if (startTimeColumn) {
          this.store.dispatch(
            updateElementProps({
              elementId: element.id,
              path: 'content.input.startTimeColumn',
              value: startTimeColumn,
            }),
          );
        }
      }),
      map(() => undefined),
    );
  }
}
